import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const usePortfolioStore = defineStore('portfolio', () => {
  const uiStore = useUIStore()

  // State
  const portfolioItems = ref([])
  const currentItem = ref(null)
  const loading = ref(false)

  // Actions
  const fetchPortfolio = async (params = {}) => {
    try {
      loading.value = true
      logService.info('Fetching portfolio list...', params)

      const response = await apiService.get('/portfolio', params)

      if (response.success) {
        portfolioItems.value = response.data
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch portfolio')
      }
    } catch (error) {
      logService.error('Fetch Portfolio Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { data: [], meta: {} }
    } finally {
      loading.value = false
    }
  }

  const fetchPortfolioItem = async (id) => {
    try {
      loading.value = true
      logService.info(`Fetching portfolio item: ${id}`)

      const response = await apiService.get(`/portfolio/${id}`)

      if (response.success) {
        currentItem.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Portfolio item not found')
      }
    } catch (error) {
      logService.error(`Fetch Portfolio Item Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const getFeaturedPortfolio = async () => {
    return await fetchPortfolio({ featured: true })
  }

  const getPortfolioByCategory = async (category) => {
    return await fetchPortfolio({ category })
  }

  const searchPortfolio = async (searchTerm) => {
    return await fetchPortfolio({ search: searchTerm })
  }

  // Getters
  const getPortfolioById = (id) => {
    return portfolioItems.value.find(item => item.id === id)
  }

  const getFeaturedItems = () => {
    return portfolioItems.value.filter(item => item.featured)
  }

  const getCategories = () => {
    const categories = [...new Set(portfolioItems.value.map(item => item.category))]
    return categories.sort()
  }

  const addPortfolio = async (portfolioData) => {
    try {
      loading.value = true
      logService.info('Adding new portfolio item...', portfolioData)

      const response = await apiService.post('/portfolio', portfolioData)

      if (response.success) {
        portfolioItems.value.unshift(response.data)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Portfolio item created successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to create portfolio item')
      }
    } catch (error) {
      logService.error('Add Portfolio Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const updatePortfolio = async (id, portfolioData) => {
    try {
      loading.value = true
      logService.info(`Updating portfolio item: ${id}`, portfolioData)

      const response = await apiService.put(`/portfolio/${id}`, portfolioData)

      if (response.success) {
        const index = portfolioItems.value.findIndex(item => item.id === id)
        if (index !== -1) {
          portfolioItems.value[index] = response.data
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Portfolio item updated successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update portfolio item')
      }
    } catch (error) {
      logService.error(`Update Portfolio Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const deletePortfolio = async (id) => {
    try {
      loading.value = true
      logService.info(`Deleting portfolio item: ${id}`)

      const response = await apiService.delete(`/portfolio/${id}`)

      if (response.success) {
        portfolioItems.value = portfolioItems.value.filter(item => item.id !== id)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Portfolio item deleted successfully'
        })
      } else {
        throw new Error(response.message || 'Failed to delete portfolio item')
      }
    } catch (error) {
      logService.error(`Delete Portfolio Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const uploadImage = async (portfolioId, file) => {
    try {
      logService.info(`Uploading image for portfolio: ${portfolioId}`)
      const formData = new FormData()
      formData.append('image', file)

      const response = await apiService.upload(`/portfolio/${portfolioId}/upload-image`, formData)

      if (response.success) {
        // Update the portfolio item in the list
        const index = portfolioItems.value.findIndex(item => item.id === portfolioId)
        if (index !== -1) {
          portfolioItems.value[index].featured_image = response.data.featured_image
          portfolioItems.value[index].featured_image_url = response.data.featured_image_url
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Image uploaded successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload image')
      }
    } catch (error) {
      logService.error(`Upload Image Error [${portfolioId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const uploadScreenshot = async (portfolioId, file, caption = null) => {
    try {
      logService.info(`Uploading screenshot for portfolio: ${portfolioId}`)
      const formData = new FormData()
      formData.append('image', file)
      if (caption) {
        formData.append('caption', caption)
      }

      const response = await apiService.upload(`/portfolio/${portfolioId}/screenshots`, formData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Screenshot uploaded successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload screenshot')
      }
    } catch (error) {
      logService.error(`Upload Screenshot Error [${portfolioId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const deleteScreenshot = async (screenshotId) => {
    try {
      logService.info(`Deleting screenshot: ${screenshotId}`)
      const response = await apiService.delete(`/portfolio/screenshots/${screenshotId}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Screenshot deleted successfully'
        })
      } else {
        throw new Error(response.message || 'Failed to delete screenshot')
      }
    } catch (error) {
      logService.error(`Delete Screenshot Error [${screenshotId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const reorderScreenshots = async (portfolioId, screenshots) => {
    try {
      logService.info(`Reordering screenshots for portfolio: ${portfolioId}`)
      const response = await apiService.put(`/portfolio/${portfolioId}/screenshots/reorder`, { screenshots })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Screenshot order updated successfully'
        })
      } else {
        throw new Error(response.message || 'Failed to reorder screenshots')
      }
    } catch (error) {
      logService.error(`Reorder Screenshots Error [${portfolioId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const attachServices = async (portfolioId, serviceIds) => {
    try {
      logService.info(`Attaching services to portfolio: ${portfolioId}`, serviceIds)
      const response = await apiService.post(`/portfolio/${portfolioId}/services`, { service_ids: serviceIds })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Services attached successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to attach services')
      }
    } catch (error) {
      logService.error(`Attach Services Error [${portfolioId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const detachService = async (portfolioId, serviceId) => {
    try {
      logService.info(`Detaching service ${serviceId} from portfolio ${portfolioId}`)
      const response = await apiService.delete(`/portfolio/${portfolioId}/services/${serviceId}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Service detached successfully'
        })
      } else {
        throw new Error(response.message || 'Failed to detach service')
      }
    } catch (error) {
      logService.error(`Detach Service Error:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  return {
    // State
    portfolioItems,
    currentItem,
    loading,

    // Actions
    fetchPortfolio,
    fetchPortfolioItem,
    getFeaturedPortfolio,
    getPortfolioByCategory,
    searchPortfolio,
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
    uploadImage,
    uploadScreenshot,
    deleteScreenshot,
    reorderScreenshots,
    attachServices,
    detachService,

    // Getters
    getPortfolioById,
    getFeaturedItems,
    getCategories
  }
})
